import { useEffect, useRef, useState } from 'react';
import type { ViewerFamilySessionGroup } from '@usacsmideo/types';
import { DayGroupedSessions } from './DayGroupedSessions';
import type { HandoffConsentProps } from './SessionCard';

/**
 * One collapsible family-member group on the Sessions Surface (CU-86aj1c9ev §2.2).
 * Default open/closed is DERIVED (not persisted): `defaultExpanded` comes from
 * the recency rule (a session within the 15-min live-session window) OR a
 * `?focus=` deep-link override — both computed by the parent. The user can then
 * toggle freely within the session.
 */
export function FamilyGroup({
  group,
  defaultExpanded,
  focusSessionId,
  now,
  onRefresh,
  isRefreshing,
  dontAskAgain,
  onRecordHandoffConsent,
  isRecordingConsent,
}: {
  group: ViewerFamilySessionGroup;
  defaultExpanded: boolean;
  focusSessionId?: string;
  /** CU-86aj3a0k2: forwarded to cards for the live-window refresh button. */
  now?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
} & HandoffConsentProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // CU-86aj3a0k2: `useState(defaultExpanded)` only seeds the INITIAL value, so a
  // `defaultExpanded` that flips true AFTER mount (a `?focus=` arriving while this
  // group is already mounted, or a refetch surfacing recent activity) would
  // otherwise be ignored — the group would stay collapsed. Force-open on the
  // false→true rising edge ONLY; never auto-collapse, so a user who manually
  // closed a still-"default-open" group isn't fought on the next render.
  const prevDefaultExpanded = useRef(defaultExpanded);
  useEffect(() => {
    if (defaultExpanded && !prevDefaultExpanded.current) {
      setExpanded(true);
    }
    prevDefaultExpanded.current = defaultExpanded;
  }, [defaultExpanded]);

  const count = group.sessions.length;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2">
          <span className="text-gray-400">{expanded ? '▼' : '▶'}</span>
          <span className="font-semibold text-sky-800">
            {group.patientDisplayName}
          </span>
        </span>
        <span className="text-xs text-gray-500">
          {count} {count === 1 ? 'scan' : 'scans'}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <DayGroupedSessions
            sessions={group.sessions}
            focusSessionId={focusSessionId}
            now={now}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            dontAskAgain={dontAskAgain}
            onRecordHandoffConsent={onRecordHandoffConsent}
            isRecordingConsent={isRecordingConsent}
          />
        </div>
      )}
    </div>
  );
}

export default FamilyGroup;
